import { defaultConfig, useConfigStore } from '@/stores/configStore';
import { useCallback, useRef } from 'react';

const useTickSound = () => {
    // Giữ AudioContext chung
    const audioCtxRef = useRef<AudioContext | null>(null);
    // Giữ promise resume để tránh gọi nhiều lần
    const resumePromiseRef = useRef<Promise<void> | null>(null);
    // Flag để tránh phát nhiều âm thanh cùng lúc
    const isPlayingRef = useRef(false);

    const getAudioCtx = async (): Promise<AudioContext> => {
        if (!audioCtxRef.current || audioCtxRef.current.state === 'closed') {
            audioCtxRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();
            resumePromiseRef.current = null;
        }

        const audioCtx = audioCtxRef.current;

        // Nếu context bị suspended, đợi resume hoàn thành
        if (audioCtx.state === 'suspended') {
            // Nếu đã có promise resume đang chạy, đợi nó
            if (resumePromiseRef.current) {
                await resumePromiseRef.current;
            } else {
                // Tạo promise resume mới
                resumePromiseRef.current = audioCtx.resume().catch((e) => {
                    console.error('Failed to resume AudioContext:', e);
                }).finally(() => {
                    resumePromiseRef.current = null;
                });
                await resumePromiseRef.current;
            }
        }

        return audioCtx;
    };

    const playTick = useCallback(async (playSound?: string, customVolume?: number) => {
        try {
            // Tránh phát nhiều âm thanh cùng lúc
            if (isPlayingRef.current) {
                return;
            }

            isPlayingRef.current = true;

            // Use custom volume if provided, otherwise get from store
            const currentVolume = customVolume ?? useConfigStore.getState()?.config?.ui?.volume ?? defaultConfig.ui.volume;

            // Đảm bảo AudioContext sẵn sàng (đợi resume nếu cần)
            const audioCtx = await getAudioCtx();

            // Kiểm tra lại state sau khi resume
            if (audioCtx.state !== 'running') {
                console.warn('AudioContext is not running after resume, state:', audioCtx.state);
                isPlayingRef.current = false;
                return;
            }

            const gainNode = audioCtx.createGain();
            gainNode.connect(audioCtx.destination);

            const effectiveVolume = (currentVolume * 2) / 10;
            const currentTime = audioCtx.currentTime;

            let isReset = false;
            const resetPlayingFlag = () => {
                if (isReset) return;
                isReset = true;
                try {
                    gainNode.disconnect();
                } catch (e) {
                    // Ignore disconnect errors
                }
                isPlayingRef.current = false;
            };

            if (playSound === 'ting') {
                // Set gain envelope for full "ting" sound duration
                gainNode.gain.setValueAtTime(effectiveVolume, currentTime);
                gainNode.gain.exponentialRampToValueAtTime(0.001, currentTime + 0.5);

                const osc2 = audioCtx.createOscillator();
                osc2.type = 'sine';
                osc2.frequency.setValueAtTime(1200, currentTime); // E6
                osc2.connect(gainNode);

                osc2.start(currentTime);
                osc2.stop(currentTime + 0.5);

                // Reset flag sau khi âm thanh kết thúc
                osc2.onended = resetPlayingFlag;
                
                // Timeout để đảm bảo flag được reset nếu onended không được gọi
                setTimeout(resetPlayingFlag, 600);
            } else {
                // Set gain envelope for tick sound
                gainNode.gain.setValueAtTime(effectiveVolume, currentTime);
                gainNode.gain.exponentialRampToValueAtTime(0.001, currentTime + 0.05);

                const oscillator = audioCtx.createOscillator();
                oscillator.type = 'sine';
                oscillator.frequency.setValueAtTime(2200, currentTime);
                oscillator.connect(gainNode);

                oscillator.start(currentTime);
                oscillator.stop(currentTime + 0.05);

                // Reset flag sau khi âm thanh kết thúc
                oscillator.onended = resetPlayingFlag;
                
                // Timeout để đảm bảo flag được reset nếu onended không được gọi
                setTimeout(resetPlayingFlag, 100);
            }
        } catch (e: unknown) {
            console.error('Web Audio API is not supported in this browser.', e);
            isPlayingRef.current = false;
        }
    }, []);
    return { playTick };
};

export default useTickSound;
